/** @jsx jsx */
import { useState, useMemo } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';

import NoHoverButton from '../../../shared/components/NoHoverButton';
import iconSet from '../../../shared/images/teambuildr-selection.json';

import { setActiveReduxModal, setSelectedGroups } from '../ducks/feedActions';
import { alphabetizeGroups } from '../helperFunctions';
import { feedTracker } from '../../../shared/utils/amplitudeHelper';

const ActionWrapper = styled('div')`
  display: flex;
  justify-content: end;
  align-items: center;
  font-weight: 500;
  font-size: 12px;
  color: #999;
  border-top: 1px solid #f0f0f0;
  border-bottom-right-radius: 50%;
  border-bottom-left-radius: 50%;
  width: 100%;
  z-index: 1;
  bottom: 0;
  background: #ffffff;
  height: 50px;
  .submit-button {
    height: 100%;
    width: 100%;
    appearance: none;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 0;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    border-bottom-right-radius: 5px;
    border-bottom-left-radius: 5px;
    background: #0dcc8a;
    color: #fff;
    font-size: 14px;
    font-weight: 900;
    padding: 6.5px 25px;
    outline: none !important;
    text-decoration: none;
  }
`;

const CloseButtonContainer = styled('div')`
  display: flex;
  justify-content: end;
  align-items: center;
  background: #ffffff;
  cursor: pointer;
  margin-right: 5px;
`;

const GroupIconWrapper = styled('div')`
  height: 50px;
  width: 50px;
  border-radius: 50%;
  border: 2px solid #444444;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GroupItem = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
  width: 100%;
  z-index: 1;
  padding: 8px;
  height: 50px;
  top: 0;
  margin-bottom: 4px;
  ${(props) => props.selected
    && `
    background-color: #CFF5E8;
    border-radius: 8px;
  `}
  &:hover {
    background-color: #eeeeee;
    border-radius: 8px;
    font-weight: 500;
  }
`;

const GroupOptionsDiv = styled('div')`
  color: #444444;
  background-color: #ffffff;
  width: 80%;
  margin: 0 auto;
  text-align: center;
`;

const GroupSearchContainer = styled('div')`
  color: #616161;
  padding: 6px 0px;
  width: 80%;
  margin: 40px auto 0 auto;
  border-radius: 3px;
`;

const GroupSearchInput = styled('input')`
  width: 100%;
  margin: auto;
  border: 1px solid #ffffff;
  border-radius: 8px;
  padding: 8px;
  background-color: #eeeeee;
  color: #444444;
`;

const GroupsContainer = styled('div')`
  width: 100%;
  margin: 0 auto;
  height: 400px;
  overflow: auto;
`;

const ModalHeaderDiv = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #ffffff;
  font-weight: 500;
  color: #444444;
  border-bottom: 1px solid #979797;
  width: 100%;
  z-index: 1;
  padding: 10px;
  height: 150px;
`;

const ModalWrapper = styled('div')`
  display: flex;
  width: 100%;
  max-width: 450px;
  border-radius: 10px;
  .ReactModal__Content {
    border-radius: 5px;
  }
`;

const NavigationWrapper = styled('div')`
  display: flex;
  justify-content: end;
  align-items: center;
  background: #ffffff;
  font-weight: 500;
  font-size: 12px;
  color: #616161;
  width: 100%;
  border-top-right-radius: 50%;
  border-top-left-radius: 50%;
  border: none;
  z-index: 1;
  padding: 10px;
  margin-top: 5px;
  height: 25px;
  top: 0;
`;

const PrimaryWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 900px;
  border-radius: 8px;
  background: #ffffff;
`;

const FilterGroupsModal = ({ onRequestClose }) => {
  const groups = useSelector((state) => state.feed.data.groups);
  const selectedGroups = useSelector((state) => state.feed.data.selectedGroups);

  const [localSelectedGroups, setLocalSelectedGroups] = useState(
    selectedGroups,
  );
  const [searchText, setSearchText] = useState('');

  const dispatch = useDispatch();

  const searchedGroups = useMemo(() => (groupsArray) => groupsArray.filter(
    (group) => group.name.toLowerCase().includes(searchText.toLowerCase()),
  ), [searchText]);

  const unselectedGroups = useMemo(() => groups.length > 0
    && groups.filter(
      (group) => !localSelectedGroups.some((localGroup) => localGroup.id === group.id),
    ), [groups, localSelectedGroups]);

  const numSelectedGroups = localSelectedGroups.length;
  const buttonText = numSelectedGroups > 0
    ? `${numSelectedGroups} ${numSelectedGroups === 1 ? 'GROUP' : 'GROUPS'}`
    : '';

  const handleCloseModal = () => {
    setSearchText('');
    onRequestClose();
  };

  const handleChangeSearch = (e) => {
    e.preventDefault();
    setSearchText(e.target.value);
  };

  const handleToggleSelected = (group) => {
    let updatedGroups = [];
    if (!localSelectedGroups.includes(group)) {
      updatedGroups = alphabetizeGroups([...localSelectedGroups, group]);
    } else {
      updatedGroups = alphabetizeGroups(localSelectedGroups.filter(
        (currentGroup) => currentGroup.id !== group.id,
      ));
    }
    setLocalSelectedGroups(updatedGroups);
  };

  const handleChangeSelectedGroups = () => {
    batch(() => {
      dispatch(setSelectedGroups(localSelectedGroups));
      dispatch(setActiveReduxModal(''));
    });
    feedTracker('TV Mode', 'Group filter submitted');
  };

  const renderSelectedGroups = () => (
    localSelectedGroups.length > 0 && (
      <GroupOptionsDiv>
        {searchedGroups(localSelectedGroups).map((group) => (
          <GroupItem
            key={group.id}
            onClick={() => handleToggleSelected(group)}
            selected
            searchedGroups={searchedGroups(groups)}
          >
            <span>{group.name}</span>
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='checkmark'
              color='#0DCC8A'
            />
          </GroupItem>
        ))}
      </GroupOptionsDiv>
    )
  );

  const renderUnselectedGroups = () => (
    <GroupOptionsDiv>
      {unselectedGroups.length > 0
        && searchedGroups(unselectedGroups).map((group) => (
          <GroupItem
            key={group.id}
            onClick={() => handleToggleSelected(group)}
            searchedGroups={searchedGroups(groups)}
          >
            <span>{group.name}</span>
          </GroupItem>
        ))}
    </GroupOptionsDiv>
  );

  const renderModalHeader = () => (
    <ModalHeaderDiv>
      <GroupIconWrapper>
        <IcomoonReact
          iconSet={iconSet}
          size={32}
          icon='group'
          color='#444444'
        />
      </GroupIconWrapper>
      <h2>Filter by Groups</h2>
    </ModalHeaderDiv>
  );

  const renderSearchSection = () => (
    <GroupSearchContainer>
      <GroupSearchInput
        type='text'
        value={searchText}
        placeholder='Search for a group'
        onChange={handleChangeSearch}
      />
    </GroupSearchContainer>
  );

  return (
    <ModalWrapper>
      <PrimaryWrapper>
        <NavigationWrapper>
          <CloseButtonContainer onClick={handleCloseModal}>
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='remove'
              color='#444444'
            />
          </CloseButtonContainer>
        </NavigationWrapper>
        {renderModalHeader()}
        {renderSearchSection()}
        <GroupsContainer>
          {renderSelectedGroups()}
          {renderUnselectedGroups()}
        </GroupsContainer>
        <ActionWrapper>
          <NoHoverButton
            label='Submit'
            cta={`SAVE ${buttonText}`}
            type='button'
            className='submit-button'
            onClick={handleChangeSelectedGroups}
            color='#0DCC8A'
          >
            Confirm
          </NoHoverButton>
        </ActionWrapper>
      </PrimaryWrapper>
    </ModalWrapper>
  );
};

FilterGroupsModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default FilterGroupsModal;
